import React, { useEffect, useState } from 'react';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { isFlushFeatureGateExposuresMessage } from '@rovo-extension/feature-gates-adapter';
import { StatSigInitialization } from '../../bootstrap/StatSigInitialization';
import { extensionUrl } from '../../config/config';
import { useUserAccount } from '../../clients/gateway-client/gatewayClient';
import { persister, queryClient } from '../../ExtensionApp';
import { useFeatureGateExposureRelay } from '../../hooks/useFeatureGateExposureRelay';
import { getSelectedSiteFromLocalStorage } from '../../hooks/useSite';
import { postMessageToParent } from '../../message-bus/postMessageToParent';
import { isAtlassianUser } from '../../utils/isAtlassianUser';
import { logWarn } from '../../utils/logger';

/**
 * Root for `EXPOSURE_RELAY_PATH`, loaded by the extension's hidden relay page. It initialises the
 * live Statsig client and listens for the exposures to log; no app shell, so a hidden load starts no
 * session replay, preloads no chat and renders nothing.
 */
export default function ExposureRelayRoot(): React.JSX.Element {
	return (
		<PersistQueryClientProvider client={queryClient} persistOptions={{ persister }}>
			<ExposureRelay />
		</PersistQueryClientProvider>
	);
}

function ExposureRelay(): React.JSX.Element {
	const { data, error: userError, isFetchedAfterMount } = useUserAccount();
	// Only an account the mandatory refetch has confirmed: a persisted one can belong to a session
	// that has since signed out or switched, and the relay page matches exposures against it.
	const userAccount = isFetchedAfterMount && !userError ? data : undefined;
	// Read once: a new site object per render changes Statsig's identifiers, and the SDK then calls
	// `updateUserWithProvider` before initialisation has created its client.
	const [site] = useState(getSelectedSiteFromLocalStorage);
	const [anonymousId] = useState<string | undefined>(
		() => JSON.parse(localStorage.getItem('ajs_anonymous_id') ?? '""') || undefined,
	);

	useEffect(() => {
		// Same precedence as `RequireAuth`: an error means signed out, even with a persisted account.
		// An empty id tells the relay page to close now, keeping its queue, instead of timing out.
		if (userError) {
			postMessageToParent({ type: 'USER_UPDATE', value: { userId: '', isAtlassianUser: false } });
		} else if (userAccount) {
			// The relay page matches this against the account that recorded the exposure.
			postMessageToParent({
				type: 'USER_UPDATE',
				value: {
					userId: userAccount.accountId,
					isAtlassianUser: isAtlassianUser(userAccount.email),
				},
			});
		}
	}, [userAccount, userError]);

	// Logs the exposures the relay page names. This route is only loaded by that page, one at a time.
	useFeatureGateExposureRelay();
	useFlushOnRequest();

	if (userError || !userAccount) {
		return <></>;
	}

	return (
		<StatSigInitialization
			analyticsAnonymousId={anonymousId}
			site={site}
			userAccount={userAccount}
		/>
	);
}

/**
 * Flushes the exposures the relay just logged, since the relay page closes soon after asking. Shutting
 * the client down is the SDK's only flush; nothing else on this page reads it.
 */
function useFlushOnRequest(): void {
	useEffect(() => {
		const handler = (event: MessageEvent): void => {
			if (event.origin !== extensionUrl || event.source !== window.parent) {
				return;
			}
			if (!isFlushFeatureGateExposuresMessage(event.data)) {
				return;
			}
			try {
				FeatureGates.shutdownStatsig();
			} catch (error) {
				logWarn('Exposure relay: flush failed', { error });
			}
		};

		window.addEventListener('message', handler);
		return (): void => window.removeEventListener('message', handler);
	}, []);
}
